import { resolveDomainEvent, toPascalCase } from './workflowSpec.js'
import { sanitizeCardDescription } from './sanitize.js'
import { buildCardDeletes } from './cardMutations.js'
import { DomainModelRole } from '../constants/cardTypes.js'
import { SchemaType } from '../constants/schema.js'

const SLOTS = {
  readModel: {
    role: DomainModelRole.READ_MODEL,
    categories: ['queries', 'domainEvents'],
    label: 'read model',
    title: 'Read model'
  },
  domainEventSchema: {
    role: DomainModelRole.DOMAIN_EVENT,
    categories: ['domainEvents'],
    label: 'domain event schema',
    title: 'Domain event schema'
  }
}

const SLOT_NAMES = Object.keys(SLOTS)
const LINKING_ROLES = Object.values(SLOTS).map(({ role }) => role)

const LINKABLE_TYPES = {
  [DomainModelRole.READ_MODEL]: [SchemaType.QUERY, SchemaType.DOMAIN_EVENT],
  [DomainModelRole.DOMAIN_EVENT]: [SchemaType.DOMAIN_EVENT]
}

const CATEGORY_TYPES = {
  queries: SchemaType.QUERY,
  domainEvents: SchemaType.DOMAIN_EVENT
}

const parseSchemaRef = (ref) => {
  const segments = ref.replace('#/', '').split('/')
  const schemasIndex = segments.indexOf('schemas')
  if (schemasIndex === -1 || schemasIndex + 2 >= segments.length) {
    return null
  }

  return { category: segments[schemasIndex + 1], key: segments[schemasIndex + 2] }
}

// Includes read model cards with no schema, which get_workflow lists as queries of their own.
const collectQueryNames = (workflow, cardTypesMap) => {
  const schemas = workflow.schemas || []
  const queryNames = new Set(schemas.filter((schema) => schema.type === SchemaType.QUERY).map((schema) => schema.name))

  for (const event of workflow.domainEvents || []) {
    for (const card of event.cards || []) {
      const text = sanitizeCardDescription(card.description || '')
      const isReadModel = cardTypesMap[card.cardTypeId]?.domainModelRole === DomainModelRole.READ_MODEL
      if (isReadModel && !card.schemaId && text && !schemas.some((schema) => schema.type === SchemaType.QUERY && schema.name.toLowerCase() === text.toLowerCase())) {
        queryNames.add(text)
      }
    }
  }

  return queryNames
}

const printedRef = (card, role, schemas, queryNames) => {
  const linked = card.schemaId ? schemas.find((schema) => schema.id === card.schemaId) : null
  if (card.schemaId && !LINKABLE_TYPES[role].includes(linked?.type)) {
    return null
  }

  let name = linked?.name
  if (!linked) {
    const text = sanitizeCardDescription(card.description || '')
    if (!text) {
      return null
    }
    const ownType = role === DomainModelRole.DOMAIN_EVENT ? SchemaType.DOMAIN_EVENT : SchemaType.QUERY
    const namesake = schemas.find((schema) => schema.type === ownType && schema.name.toLowerCase() === text.toLowerCase())
    if (namesake && role === DomainModelRole.READ_MODEL) {
      // get_workflow leaves such a card out of the event's read models.
      return null
    }
    name = namesake?.name || text
  }

  if (role === DomainModelRole.DOMAIN_EVENT) {
    return { category: 'domainEvents', name }
  }
  const category = linked?.type === SchemaType.DOMAIN_EVENT && !queryNames.has(name) ? 'domainEvents' : 'queries'

  return { category, name }
}

const findLinks = (workflow, cardTypesMap, queryNames, { category, key }) => {
  const schemas = workflow.schemas || []
  const domainEvents = workflow.domainEvents || []
  const links = []

  for (let eventIndex = 0; eventIndex < domainEvents.length; eventIndex++) {
    const event = domainEvents[eventIndex]
    const cards = event.cards || []
    for (let cardIndex = 0; cardIndex < cards.length; cardIndex++) {
      const card = cards[cardIndex]
      const role = cardTypesMap[card.cardTypeId]?.domainModelRole
      const printed = LINKING_ROLES.includes(role) ? printedRef(card, role, schemas, queryNames) : null
      // Matched by printed name, not schema id: get_workflow gives same-named schemas a single ref.
      if (printed && printed.category === category && toPascalCase(printed.name) === key) {
        links.push({ card, event, eventIndex, cardIndex, role, name: printed.name })
      }
    }
  }

  return links
}

export const buildDetachSchemasMutation = ({ workflow, workflowId, detachments, cardTypes }) => {
  if (!Array.isArray(detachments) || detachments.length === 0) {
    return { error: 'detachments is required and must be a non-empty array' }
  }

  const cardTypesMap = Object.fromEntries((cardTypes || []).map((cardType) => [cardType.id, cardType]))
  const schemas = workflow.schemas || []
  const queryNames = collectQueryNames(workflow, cardTypesMap)
  const matches = []
  const targets = new Map()
  const descriptions = []

  for (const entry of detachments) {
    const { domainEvent, aggregateRoot } = entry || {}

    if (aggregateRoot !== undefined) {
      return { error: 'An aggregate root is taken off an event with update_domain_event, by setting aggregateRoot to an empty string' }
    }

    const givenSlots = SLOT_NAMES.filter((slotName) => entry?.[slotName] !== undefined)
    if (givenSlots.length !== 1) {
      return { error: 'Each detachment must have exactly one of readModel or domainEventSchema' }
    }
    const [slotName] = givenSlots
    const slot = SLOTS[slotName]
    const ref = entry[slotName]

    if (!ref || typeof ref !== 'string') {
      return { error: `Each detachment's ${slotName} must be a $ref path` }
    }
    if (!domainEvent || typeof domainEvent !== 'string') {
      return { error: 'Each detachment must have a domainEvent ref' }
    }

    const target = parseSchemaRef(ref)
    if (!target || !slot.categories.includes(target.category)) {
      return { error: `${slot.title} not found for ref: ${ref}` }
    }

    const event = resolveDomainEvent(domainEvent, workflow)
    if (!event) {
      return { error: `Domain event not found for ref: ${domainEvent}` }
    }

    const links = findLinks(workflow, cardTypesMap, queryNames, target)
    const schema = schemas.find((candidate) => candidate.type === CATEGORY_TYPES[target.category] && toPascalCase(candidate.name) === target.key)
    const name = links[0]?.name || schema?.name
    if (!name) {
      return { error: `${slot.title} not found for ref: ${ref}` }
    }

    const onEvent = links.filter((link) => link.event === event && link.role === slot.role)
    if (onEvent.length === 0) {
      return { error: `${slot.title} "${name}" is not attached to event "${event.description}"` }
    }
    if (matches.some((match) => match.card === onEvent[0].card)) {
      return { error: `${slot.title} "${name}" is detached from event "${event.description}" more than once in the batch` }
    }

    matches.push(...onEvent)
    targets.set(`${target.category}/${target.key}`, { name, links, hasSchema: Boolean(schema) })
    descriptions.push(`${slot.label} "${name}" from "${event.description}"`)
  }

  const detachedCards = new Set(matches.map(({ card }) => card))
  const unattached = [...targets.values()]
    .filter(({ links, hasSchema }) => hasSchema && links.every(({ card }) => detachedCards.has(card)))
    .map(({ name }) => name)

  let message = `Detached ${descriptions.join(', ')}`
  if (unattached.length > 0) {
    message += `. No longer on any event, still in the workflow: ${unattached.map((name) => `"${name}"`).join(', ')}`
  }

  return { cardDeletes: buildCardDeletes(workflowId, matches), unattached, message }
}
